import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'
import { RadioGroupItem } from '#src/components/core/radio-group/RadioGroupItem.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface GAD2FormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* No `renderFieldSuggestions`: this form renders its questions as a MATRIX —
   * one row of column headers (Yes/No/N/A) above bare, label-less radios — so
   * there is no per-option row for a suggestion's controls to anchor to.
   * `ValidatedToolInnerForm` absorbs the prop, so the router can go on handing
   * it to every form without it reaching the DOM. */
  restorationId?: string
}

const gad2AnswerEnum = z
  .enum([
    'not-at-all',
    'several-days',
    'more-than-half-the-days',
    'nearly-every-day',
  ])
  .nullable()

const schema = z.object({
  'nervous-anxious': gad2AnswerEnum,
  'control-worrying': gad2AnswerEnum,
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.gad2Questionnaire)

const GAD2_QUESTIONS: {
  linkId: keyof z.infer<typeof schema>
  number: number
}[] = [
  { linkId: 'nervous-anxious', number: 1 },
  { linkId: 'control-worrying', number: 2 },
]

const ANSWER_COLUMNS = [
  { value: 'not-at-all', label: 'Not at all', sublabel: '(0)' },
  { value: 'several-days', label: 'Several days', sublabel: '(1)' },
  {
    value: 'more-than-half-the-days',
    label: 'More than half the days',
    sublabel: '(2)',
  },
  {
    value: 'nearly-every-day',
    label: 'Nearly every day',
    sublabel: '(3)',
  },
] as const

export function GAD2Form({
  response,
  prefillValuesByLinkId,
  restorationId,
  ...delegated
}: GAD2FormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle={validatedTool.getItemText('instructions')}
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection title="GAD-2 Screening">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-x-4 gap-y-0 border-b pb-2">
          <div />
          {ANSWER_COLUMNS.map((col) => (
            <div
              key={col.value}
              className="w-20 text-center text-sm font-semibold"
            >
              {col.label}
              <br />
              {col.sublabel}
            </div>
          ))}
        </div>
        {GAD2_QUESTIONS.map(({ linkId, number }) => (
          <FormField
            key={linkId}
            control={form.control}
            name={linkId}
            render={({ field: { value, ...rest }, fieldState }) => (
              <div
                className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-x-4 border-b py-2 ${fieldState.error ? 'rounded px-2 ring-1 ring-destructive' : ''}`}
              >
                <div className="text-sm">
                  <span className="font-semibold">{number}.</span>{' '}
                  {validatedTool.getItemText(linkId)}
                </div>
                <RadioGroup
                  className="contents"
                  value={value ?? undefined}
                  {...rest}
                >
                  {ANSWER_COLUMNS.map((col) => (
                    <label
                      key={col.value}
                      className="flex w-20 cursor-pointer items-center justify-center py-2"
                    >
                      <RadioGroupItem value={col.value} />
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
          />
        ))}
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

const COLORS = {
  'Positive Screen': 'red',
  'Negative Screen': 'green',
} as const satisfies Record<questionnaires.Gad2Category, string>

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const label = questionnaires.gad2Category(result.score)
  const color = label ? COLORS[label] : ('gray' as const)

  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label ?? 'Not Scored'}
      </Badge>
    </div>
  )
}
